import Form from 'react-bootstrap/Form';
import { FiledType, IChoice, IErrors, IMeta, IPolicy, ITemplate, IUserTemplateCategory, IVarPrompt } from './models';
import UploadField from './UploadField';
import PasswordField from './PasswordField';
import { useEffect, useState } from "react";
import { apiGet } from "./api";
import { getTemplateCategory } from "./templates/category";
import { InputField, NumberField } from './components/input';
import { TextareaField } from './components/textarea';
import { SwitchField } from './components/checkbox';
import { SelectField } from './components/select';
import type { SelectOption } from './components/select';
import AssetTypesField from "./components/fields/AssetTypesField";

export const TEXTAREA_ROWS = 8;


interface IPolicyFormProps {
    meta: IMeta,
    policy: IPolicy,
    template?: ITemplate,
    userTemplateCategory: IUserTemplateCategory,
    errors: IErrors,
    onChange: ((name: string, value: any, type_field?: string) => void),
    canChange: boolean,
}


export default function PolicyForm({ meta, policy, template, userTemplateCategory, errors, onChange, canChange }: IPolicyFormProps) {
    const [templates, setTemplates] = useState<ITemplate[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [templateCategory, setTemplateCategory] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!template) return;
        setTemplateCategory(getTemplateCategory(template.yaml));
    }, [template]);

    useEffect(() => {
        apiGet<ITemplate[]>('/automation/api/templates/')
            .then(setTemplates)
            .catch(() => setTemplates([]));
    }, []);

    useEffect(() => {
        setCategories(flattenCategoryTree(templates));
    }, [templates]);

    const flattenCategoryTree = (templates: ITemplate[]): string[] => {
        const categories: Set<string> = new Set();

        templates.forEach((template) => {
            const parts = template.name.split(' \\ ');
            for (let i = 1; i < parts.length; i++) {
                const category = parts.slice(0, i).join(' \\ ');
                categories.add(category);
            }
        });

        return Array.from(categories).sort();
    };

    const userTemplateCategoryInvalid = () => {
        return Boolean(userTemplateCategory.selectedCategory && userTemplateCategory.inputtedCategory);
    }

    const updateField = (ev: any) => {
        onChange(ev.target.name, ev.target.value)
    }

    const updateChecked = (ev: any) => {
        onChange(ev.target.name, ev.target.checked)
    }

    const updateFile = (name: string, file: any) => {
        onChange(name, file);
    }

    const deleteFile = (name: string, _guid: any) => {
        onChange(name, null);
    }

    const updatePassword = (name: string, password: string) => {
        onChange(name, password)
    }

    const fieldFactory = (prompt: IVarPrompt): any => {
        let value = policy.vars[prompt.name] || prompt.default || "";
        switch (prompt.type) {
            case FiledType.File: {
                return (
                    <UploadField
                        name={prompt.name}
                        label={prompt.label}
                        onSuccess={updateFile}
                        onDelete={deleteFile}
                        files={policy.file ? [policy.file] : []}
                        required={prompt.required}
                        canChange={canChange}
                        repository={true}
                        keepOnServer={true}
                    />
                );
            }
            case FiledType.CheckBox: {
                return (
                    <SwitchField
                        name={prompt.name}
                        checked={value}
                        label={prompt.label}
                        disabled={!canChange}
                        onChange={updateChecked}
                    />
                );
            }
            case FiledType.TextArea: {
                return (
                    <TextareaField
                        name={prompt.name}
                        value={value}
                        label={prompt.label}
                        helperText={prompt.help}
                        error={errors.fields[prompt.name]}
                        rows={TEXTAREA_ROWS}
                        disabled={!canChange}
                        required={prompt.required}
                        onChange={(e: any) => onChange(prompt.name, e.target.value)}
                    />
                );
            }
            case FiledType.List: {
                const initValues: SelectOption[] = value
                    ? value.split(', ').map((v: string) => ({ value: v, label: v }))
                    : [];
                return (
                    <SelectField
                        mode="lookup-chips"
                        name={prompt.name}
                        label={prompt.label}
                        lookupUrl={prompt.lookup_handler || ''}
                        initialValues={initValues}
                        creatable
                        floating
                        disabled={!canChange}
                        required={prompt.required}
                        error={errors.fields[prompt.name]}
                        onChange={(_values: string[], options: SelectOption[]) => {
                            onChange(prompt.name, options.map(o => o.label).join(', '))
                        }}
                    />
                )
            }
            case FiledType.package: {
                const initPkgValue: SelectOption | null = value
                    ? { value, label: value }
                    : null;
                return (
                    <SelectField
                        mode="lookup"
                        name={prompt.name}
                        label={prompt.label}
                        lookupUrl={prompt.lookup_handler || ''}
                        initialValue={initPkgValue}
                        creatable
                        floating
                        disabled={!canChange}
                        required={prompt.required}
                        error={errors.fields[prompt.name]}
                        onChange={(_v: string, option: SelectOption | null) => {
                            onChange(prompt.name, option?.label ?? '')
                        }}
                    />
                )
            }

            case FiledType.Password: {
                return (
                    <PasswordField
                        name={prompt.name}
                        value={policy.vars[prompt.name]}
                        label={prompt.label}
                        helperText={prompt.help}
                        error={errors.fields[prompt.name]}
                        disabled={!canChange}
                        onChange={updatePassword}
                        required={prompt.required}
                    />
                )
            }

            case FiledType.Number: {
                const isPositive = prompt.type_options.includes('positive');
                return (
                    <NumberField
                        name={prompt.name}
                        value={value}
                        label={prompt.label}
                        helperText={prompt.help}
                        min={isPositive ? 0 : undefined}
                        step={1}
                        disabled={!canChange}
                        required={prompt.required}
                        error={errors.fields[prompt.name]}
                        onChange={(e: any) => onChange(prompt.name, e.target.value)}
                    />
                )
            }

            case FiledType.String: {

                if (prompt.choices.length) {
                    if (!value) policy.vars[prompt.name] = prompt.choices[0].name;
                    const choiceOptions = prompt.choices.map((item: IChoice) => ({
                        value: item.name,
                        label: item.label,
                    }));
                    return (
                        <SelectField
                            mode="static"
                            name={prompt.name}
                            label={prompt.label}
                            options={choiceOptions}
                            initialValue={value || prompt.choices[0].name}
                            floating
                            disabled={!canChange}
                            required={prompt.required}
                            error={errors.fields[prompt.name]}
                            onChange={(v: string) => onChange(prompt.name, v)}
                        />
                    )
                } else {
                    return (
                        <InputField
                            name={prompt.name}
                            value={value}
                            label={prompt.label}
                            helperText={prompt.help}
                            error={errors.fields[prompt.name]}
                            disabled={!canChange}
                            required={prompt.required}
                            onChange={(e: any) => onChange(prompt.name, e.target.value)}
                        />
                    )
                }
            }
            case FiledType.Assets: {
                return <AssetTypesField name={prompt.name} value={value} disabled={!canChange} required={prompt.required} onChange={onChange} />
            }
        }
    }

    const errorsFactory = (prompt: IVarPrompt): any => {
        if (!errors.fields[prompt.name]) return;
        return (
            <div style={{ "display": "block" }} className="invalid-feedback">
                {errors.fields[prompt.name]}
            </div>
        );
    }

    const helpsFactory = (prompt: IVarPrompt): any => {
        if (!prompt.help) return;

        const helpLinks = prompt.helpLink?.split(' ').map((link, index) => (
            <a key={index} target="_blank" rel="noopener noreferrer" href={link}>
                <i className='text-info fas fa-question-circle'></i>
            </a>
        ));

        return (
            <Form.Text className="text-muted">
                {prompt.help}
                &nbsp;
                {helpLinks}
            </Form.Text>
        );
    }

    return (
        <Form validated={!errors.ok}>
            <div className="pt-2">
                <InputField
                    name="name"
                    value={policy.name}
                    label="Название"
                    helperText="Название политики"
                    disabled={!canChange}
                    required={true}
                    error={errors.fields["name"]}
                    onChange={updateField}
                />
            </div>

            {meta.vars_prompt.filter(m => !m.private).map((prompt, idx) => {
                const hasBuiltInLabel = prompt.type === FiledType.CheckBox
                    || prompt.type === FiledType.TextArea
                    || prompt.type === FiledType.String
                    || prompt.type === FiledType.Number
                    || prompt.type === FiledType.List
                    || prompt.type === FiledType.package
                    || prompt.type === FiledType.Password;
                return (
                    <Form.Group key={idx} className={"pt-2"}>
                        {!hasBuiltInLabel && <Form.Label>{prompt.label}</Form.Label>}
                        {fieldFactory(prompt)}
                        {!hasBuiltInLabel && errorsFactory(prompt)}
                        {!hasBuiltInLabel && helpsFactory(prompt)}
                    </Form.Group>
                )
            })}

            {template &&
                <Form.Group className={"pt-3"}>
                    <Form.Label>
                        Категория шаблона
                        <span className="d-inline-block" style={{verticalAlign: "middle"}} data-tooltip='Если не выбрать или не создать категорию, то шаблону будет присвоена категория "Пользовательские".'>
                            <i className="rd-icon-help ml-1" style={{fontSize: "20px", color: "#D32D2F"}}></i>
                        </span>
                    </Form.Label>

                    <br />

                    <Form.Label>
                        Текущая категория:&nbsp;
                        <span style={{color: "var(--rd-text-accent)"}}>
                            {templateCategory || "Отсутствует"}
                        </span>
                        {
                            template.system &&
                            <span className='rd-tpl-badge rd-tpl-badge--system ml-1'>системная</span>
                        }
                    </Form.Label>

                    {!template.system &&
                        <div>
                            <Form.Text className="mb-2 mt-0">
                                Выберите категорию из существующих
                                <span className="d-inline-block" data-tooltip='Все категории хранятся только в текущий момент времени. При замене старой категории на новую, старая категория удаляется, если другие шаблоны не используют ее.'>
                                    <i className="rd-icon-help ml-1" style={{fontSize: "20px", color: "#D32D2F"}}></i>
                                </span>
                            </Form.Text>
                            {categories &&
                                <div>
                                    <SelectField
                                        mode="static"
                                        name="selectedCategory"
                                        placeholder="Выберите категорию"
                                        options={categories.map((c) => ({ value: c, label: c }))}
                                        initialValue={userTemplateCategory.selectedCategory}
                                        disabled={!canChange}
                                        error={userTemplateCategoryInvalid() ? errors.categoryError?.message : undefined}
                                        searchable
                                        clearable
                                        onChange={(value: string) => onChange("selectedCategory", value)}
                                    />

                                    <Form.Text className="my-2">
                                        Или введите новую категорию
                                    </Form.Text>
                                </div>
                            }

                            <InputField
                                name="inputtedCategory"
                                value={userTemplateCategory.inputtedCategory || ''}
                                label="Новая категория"
                                helperText={'Для подкатегорий используйте "\\". Например, "Категория \\ Подкатегория"'}
                                error={userTemplateCategoryInvalid() ? errors.fields["category"] : undefined}
                                disabled={!canChange}
                                required={false}
                                onChange={updateField}
                            />
                        </div>
                    }
                </Form.Group>
            }
        </Form>
    );
}
